import { CommonModule } from '@angular/common';
import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RUNTIME_ENVIRONMENT } from 'environments-lib';
import { IRuntimeEnvironment } from 'interfaces-lib';
import { Observable, Subscription } from 'rxjs';
import { UtilsService } from 'utils-service-lib';
import { FilePreviewComponent, FilePreviewData } from '../file-preview.component';

// Preview data that is not ready yet. The modal opens immediately, shows a spinner and
// renders the preview once data$ emits. Lets a caller open the modal before it starts a
// long running generation, so the user cannot trigger it more than once.
export interface IFilePreviewAsyncData {
  filename?: string; // placeholder shown in the header while loading
  loadingMessage?: string; // defaults to 'Generating preview…'
  data$: Observable<FilePreviewData>;
}

export type FilePreviewModalData = FilePreviewData | IFilePreviewAsyncData;

const LOADING_MESSAGE = 'Generating preview…';
const DEFAULT_ERROR_MESSAGE = 'Failed to load preview.';

@Component({
  selector: 'lib-file-preview-modal',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, FilePreviewComponent],
  templateUrl: './file-preview-modal.component.html',
  styleUrl: './file-preview-modal.component.scss',
})
export class FilePreviewModalComponent implements OnInit, OnDestroy {
  data: FilePreviewData | null = null;
  filename = '';
  isLoading = false;
  errorMessage: string | null = null;
  loadingMessage = LOADING_MESSAGE;

  private dataSubscription: Subscription | null = null;

  constructor(
    private dialogRef: MatDialogRef<FilePreviewModalComponent>,
    private utilsService: UtilsService,
    @Inject(MAT_DIALOG_DATA) private dialogData: FilePreviewModalData,
    @Inject(RUNTIME_ENVIRONMENT) public runtimeEnvironment: IRuntimeEnvironment,
  ) {}

  ngOnInit(): void {
    if (this.isAsyncData(this.dialogData)) {
      this.loadAsyncData(this.dialogData);
      return;
    }

    this.data = this.dialogData;
    this.filename = this.dialogData?.filename ?? '';
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
    this.dataSubscription = null;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onDownload(): void {
    if (!this.data?.originalFileContent) {
      console.error('No original file content available for download');
      return;
    }

    // Create a download link
    const url = URL.createObjectURL(this.data.originalFileContent);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.data.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
  }

  // TODO: solve the issue with the parent modal closing
  @HostListener('document:keydown', ['$event'])
  handleKeydownEvent(event: KeyboardEvent) {
    if (!this.utilsService.escapableElementActive()) {
      if (event.code === 'Escape') {
        this.dialogRef.close();
      }
    } else {
      this.utilsService.escapableElementActive(false);
    }
  }

  getRenderMode(): string | '' {
    // Handle the case where runtimeEnvironment might be undefined and return a default value
    if (this.runtimeEnvironment) {
      return this.runtimeEnvironment.renderMode;
    } else {
      return '';
    }
  }

  private isAsyncData(data: FilePreviewModalData): data is IFilePreviewAsyncData {
    return !!data && 'data$' in data && !!data.data$;
  }

  private loadAsyncData(asyncData: IFilePreviewAsyncData): void {
    this.isLoading = true;
    this.filename = asyncData.filename ?? LOADING_MESSAGE;
    this.loadingMessage = asyncData.loadingMessage ?? LOADING_MESSAGE;

    this.dataSubscription = asyncData.data$.subscribe({
      next: (previewData) => {
        this.data = previewData;
        this.filename = previewData?.filename ?? '';
        this.isLoading = false;
      },
      error: (error) => {
        this.showError(error?.message || error?.toString() || DEFAULT_ERROR_MESSAGE);
      },
      complete: () => {
        if (!this.data) {
          this.showError(DEFAULT_ERROR_MESSAGE);
        }
      },
    });
  }

  private showError(message: string): void {
    this.isLoading = false;
    this.errorMessage = message;
  }
}
